import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { api } from '@watchparty/shared';
import { Input, Textarea, FormField } from '@/design-system/Inputs';
import { Button } from '@/design-system/Button';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_KEY ?? '';
const CONTACT_EMAIL = 'support@watchparty.us';
const sectionHeadingStyle = {
    fontFamily: 'var(--font-family-heading)',
    fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
    fontWeight: 700,
    color: 'var(--color-text)',
    marginBottom: '12px',
    lineHeight: 1.3,
};
const paragraphStyle = {
    fontFamily: 'var(--font-family-body)',
    fontSize: '0.9375rem',
    lineHeight: 1.7,
    color: 'var(--color-text-muted)',
};
const faqQuestionStyle = {
    fontFamily: 'var(--font-family-heading)',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: '6px',
    lineHeight: 1.4,
};
const faqAnswerStyle = {
    ...paragraphStyle,
    marginBottom: '28px',
};
export function SupportPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const turnstileRef = useRef(null);
    const widgetIdRef = useRef(null);
    useEffect(() => {
        if (!TURNSTILE_SITE_KEY)
            return;
        function renderWidget() {
            if (turnstileRef.current && window.turnstile && widgetIdRef.current === null) {
                widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token) => setTurnstileToken(token),
                    'expired-callback': () => setTurnstileToken(''),
                    theme: 'dark',
                    appearance: 'managed',
                });
            }
        }
        if (window.turnstile) {
            renderWidget();
        }
        else {
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            script.async = true;
            script.onload = renderWidget;
            document.head.appendChild(script);
        }
        return () => {
            if (widgetIdRef.current !== null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);
    function validate() {
        const errors = {};
        if (!email.trim()) {
            errors.email = 'Email is required';
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            errors.email = 'Please enter a valid email address';
        }
        if (!subject.trim()) {
            errors.subject = 'Subject is required';
        }
        else if (subject.trim().length < 5) {
            errors.subject = 'Subject must be at least 5 characters';
        }
        else if (subject.trim().length > 100) {
            errors.subject = 'Subject must be 100 characters or less';
        }
        if (!message.trim()) {
            errors.message = 'Message is required';
        }
        else if (message.trim().length < 20) {
            errors.message = 'Message must be at least 20 characters';
        }
        else if (message.trim().length > 2000) {
            errors.message = 'Message must be 2000 characters or less';
        }
        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            errors.turnstile = 'Please complete the verification';
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }
    async function handleSubmit(e) {
        e.preventDefault();
        setErrorMessage('');
        if (!validate())
            return;
        setIsSubmitting(true);
        try {
            await api.post('/support', {
                name: name.trim() || null,
                email: email.trim(),
                subject: subject.trim(),
                message: message.trim(),
                deviceInfo: navigator.userAgent,
                turnstileToken,
                website,
            });
            setSubmitted(true);
        }
        catch (err) {
            setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
                setTurnstileToken('');
            }
        }
        setIsSubmitting(false);
    }
    return (_jsxs("div", { style: {
            minHeight: '100vh',
            background: 'var(--color-bg)',
        }, children: [_jsx("header", { style: {
                    padding: '20px 24px',
                    borderBottom: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                }, children: _jsx("div", { style: { maxWidth: '720px', margin: '0 auto' }, children: _jsxs(Link, { to: "/", style: {
                            fontFamily: 'var(--font-family-heading)',
                            fontSize: '1.125rem',
                            fontWeight: 700,
                            textDecoration: 'none',
                        }, children: [_jsx("span", { style: { color: 'var(--color-text)' }, children: "Watch" }), _jsx("span", { style: { color: 'var(--color-accent)' }, children: "Party" })] }) }) }), _jsxs("main", { style: {
                    maxWidth: '720px',
                    margin: '0 auto',
                    padding: '48px 24px 80px',
                }, children: [_jsx("h1", { style: {
                            fontFamily: 'var(--font-family-heading)',
                            fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                            fontWeight: 700,
                            color: 'var(--color-text)',
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em',
                        }, children: "Support" }), _jsx("p", { style: {
                            ...paragraphStyle,
                            marginTop: '16px',
                            fontSize: '1.0625rem',
                            lineHeight: 1.6,
                        }, children: "Need help with WatchParty? We're here for it." }), _jsxs("section", { style: { marginTop: '48px' }, children: [_jsx("h2", { style: sectionHeadingStyle, children: "Contact us" }), _jsx("p", { style: paragraphStyle, children: "If you're running into an issue or have a question that isn't covered below, send us a message and we'll get back to you as soon as we can." }), submitted ? (_jsxs("div", { style: {
                                    marginTop: '24px',
                                    padding: '24px',
                                    background: 'var(--color-surface)',
                                    borderRadius: 'var(--radius-lg)',
                                    border: '1px solid var(--color-border)',
                                }, children: [_jsx("p", { style: {
                                            fontFamily: 'var(--font-family-heading)',
                                            fontSize: '1.125rem',
                                            fontWeight: 600,
                                            color: 'var(--color-text)',
                                            marginBottom: '8px',
                                        }, children: "Message sent" }), _jsxs("p", { style: paragraphStyle, children: ["Thanks for reaching out. We'll get back to you at ", _jsx("strong", { style: { color: 'var(--color-text)' }, children: email }), " as soon as we can."] })] })) : (_jsxs("form", { onSubmit: handleSubmit, style: {
                                    marginTop: '24px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '20px',
                                }, children: [_jsxs("div", { style: { position: 'absolute', left: '-9999px' }, "aria-hidden": "true", children: [_jsx("label", { htmlFor: "website", children: "Website" }), _jsx("input", { id: "website", name: "website", type: "text", tabIndex: -1, autoComplete: "off", value: website, onChange: (e) => setWebsite(e.target.value) })] }), _jsx(FormField, { label: "Name", htmlFor: "support-name", hint: "Optional", children: _jsx(Input, { id: "support-name", type: "text", placeholder: "Your name", value: name, onChange: (e) => setName(e.target.value), autoComplete: "name" }) }), _jsx(FormField, { label: "Email", htmlFor: "support-email", error: fieldErrors.email, children: _jsx(Input, { id: "support-email", type: "email", placeholder: "you@example.com", value: email, onChange: (e) => { setEmail(e.target.value); setFieldErrors(f => ({ ...f, email: '' })); }, autoComplete: "email", error: !!fieldErrors.email }) }), _jsx(FormField, { label: "Subject", htmlFor: "support-subject", error: fieldErrors.subject, children: _jsx(Input, { id: "support-subject", type: "text", placeholder: "What's this about?", value: subject, onChange: (e) => { setSubject(e.target.value); setFieldErrors(f => ({ ...f, subject: '' })); }, maxLength: 100, error: !!fieldErrors.subject }) }), _jsx(FormField, { label: "Message", htmlFor: "support-message", error: fieldErrors.message, hint: !fieldErrors.message ? `${message.length} / 2000` : undefined, children: _jsx(Textarea, { id: "support-message", placeholder: "Describe your issue or question in detail...", value: message, onChange: (e) => { setMessage(e.target.value); setFieldErrors(f => ({ ...f, message: '' })); }, maxLength: 2000, rows: 6, error: !!fieldErrors.message, style: { minHeight: 140 } }) }), _jsx("div", { ref: turnstileRef }), fieldErrors.turnstile && (_jsx("span", { style: {
                                            fontFamily: 'var(--font-family)',
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-danger)',
                                        }, children: fieldErrors.turnstile })), errorMessage && (_jsx("p", { style: {
                                            fontFamily: 'var(--font-family-body)',
                                            fontSize: '0.875rem',
                                            color: 'var(--color-danger)',
                                            lineHeight: 1.5,
                                        }, children: errorMessage })), _jsx(Button, { type: "submit", variant: "primary", size: "lg", loading: isSubmitting, fullWidth: true, children: "Send Message" })] }))] }), _jsxs("section", { style: { marginTop: '48px' }, children: [_jsx("h2", { style: sectionHeadingStyle, children: "Frequently asked questions" }), _jsxs("div", { style: { marginTop: '24px' }, children: [_jsx("h3", { style: faqQuestionStyle, children: "I can't sign in to my account" }), _jsx("p", { style: faqAnswerStyle, children: "Make sure you're using the same sign-in method you used when you created your account. If you signed up with Google or Apple, use that same option \u2014 an email/password login won't work for OAuth accounts. If you created an account with an email and password, check that your email is verified (look for a verification email in your inbox or spam folder). You can also reset your password from the sign-in screen." }), _jsx("h3", { style: faqQuestionStyle, children: "Sign in with Apple isn't working" }), _jsx("p", { style: faqAnswerStyle, children: "If you chose \"Hide My Email\" when you first signed in with Apple, Apple generated a private relay address for your account. Make sure you're signing in with the same Apple ID. If you've revoked WatchParty's access in your Apple ID settings, you may need to re-authorize it under Settings > Apple ID > Sign-In & Security > Sign in with Apple." }), _jsx("h3", { style: faqQuestionStyle, children: "I'm not receiving notifications" }), _jsx("p", { style: faqAnswerStyle, children: "Check that notifications are enabled for WatchParty in your device settings (Settings > Notifications > WatchParty on iOS). Inside the app, go to Account and make sure your notification preferences are turned on for the types of activity you want to hear about." }), _jsx("h3", { style: faqQuestionStyle, children: "How do parties work?" }), _jsx("p", { style: faqAnswerStyle, children: "A party is a private group where you and your friends track movies and TV shows together. Create a party, invite people with a share link, and start adding titles. Everyone in the party can see what others have watched, are watching, or want to watch \u2014 and you can discuss each title in its own thread." }), _jsx("h3", { style: faqQuestionStyle, children: "How do I delete my account?" }), _jsxs("p", { style: faqAnswerStyle, children: ["You can delete your account from the Account screen in the app. This will permanently remove your profile, party memberships, and all associated data. If you need help, email us at ", CONTACT_EMAIL, " and we'll take care of it within 30 days."] })] })] }), _jsx("section", { style: { marginTop: '48px' }, children: _jsx(Link, { to: "/", style: {
                                fontFamily: 'var(--font-family-body)',
                                fontSize: '0.9375rem',
                                color: 'var(--color-accent)',
                                textDecoration: 'none',
                            }, children: "\u2190 Back to WatchParty" }) }), _jsx("hr", { style: {
                            marginTop: '48px',
                            border: 'none',
                            borderTop: '1px solid var(--color-border)',
                        } }), _jsxs("p", { style: {
                            marginTop: '16px',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem',
                            color: 'var(--color-text-faint)',
                        }, children: ["\u00A9 ", new Date().getFullYear(), " ", _jsx("span", { style: { color: 'var(--color-text-faint)' }, children: "Watch" }), _jsx("span", { style: { color: 'var(--color-accent)', opacity: 0.5 }, children: "Party" }), ". All rights reserved."] })] })] }));
}
